import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Badge,
  DataTable,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../api';
import KPICard from '../components/KPICard';

interface UserDetailData {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    shopifyId: string;
    createdAt: string;
    onboardedAt: string | null;
  };
  points: { balance: number; lifetimeEarned: number };
  streak: { currentStreak: number; longestStreak: number };
  garden: { totalTrees: number } | null;
  contracts: Array<{ id: string; status: string; shopifyContractId: string }>;
  subscriptions: Array<{ id: string; status: string; frequencyDays: number }>;
  devices: Array<{ id: string; token: string; platform: string; active: boolean }>;
  recentPoints: Array<{ amount: number; reason: string; createdAt: string }>;
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    apiGet<UserDetailData>(`/panel-api/users/${id}`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Page title="User Detail" backAction={{ content: 'Users', onAction: () => navigate('/users') }}>
        <Card><Spinner size="large" /></Card>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="User Detail" backAction={{ content: 'Users', onAction: () => navigate('/users') }}>
        <Banner tone="critical"><p>{error || 'User not found'}</p></Banner>
      </Page>
    );
  }

  const { user, points, streak, garden, contracts, subscriptions, devices, recentPoints } = data;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'No name';

  return (
    <Page
      title={fullName}
      subtitle={user.email}
      backAction={{ content: 'Users', onAction: () => navigate('/users') }}
    >
      <Layout>
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <KPICard title="Points Balance" value={points.balance} />
            <KPICard title="Lifetime Earned" value={points.lifetimeEarned} />
            <KPICard title="Current Streak" value={`${streak.currentStreak}d`} />
            <KPICard title="Garden Trees" value={garden?.totalTrees ?? 0} />
          </InlineGrid>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Contracts</Text>
              {contracts.length === 0 ? (
                <Text as="p" tone="subdued">No contracts</Text>
              ) : (
                contracts.map((c) => (
                  <BlockStack key={c.id} gap="100">
                    <Text as="p" variant="bodySm">{c.shopifyContractId}</Text>
                    <Badge tone={c.status === 'active' ? 'success' : undefined}>
                      {c.status}
                    </Badge>
                  </BlockStack>
                ))
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Subscriptions</Text>
              {subscriptions.length === 0 ? (
                <Text as="p" tone="subdued">No subscriptions</Text>
              ) : (
                subscriptions.map((s) => (
                  <BlockStack key={s.id} gap="100">
                    <Text as="p" variant="bodySm">
                      Every {s.frequencyDays} days
                    </Text>
                    <Badge tone={s.status === 'active' ? 'success' : undefined}>
                      {s.status}
                    </Badge>
                  </BlockStack>
                ))
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Devices</Text>
              {devices.length === 0 ? (
                <Text as="p" tone="subdued">No registered devices</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Platform', 'Token (preview)', 'Active']}
                  rows={devices.map((d) => [
                    d.platform,
                    d.token.slice(0, 20) + '...',
                    d.active ? '✅' : '❌',
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Recent Points Activity</Text>
              {recentPoints.length === 0 ? (
                <Text as="p" tone="subdued">No activity</Text>
              ) : (
                <DataTable
                  columnContentTypes={['numeric', 'text', 'text']}
                  headings={['Amount', 'Reason', 'Date']}
                  rows={recentPoints.map((p) => [
                    p.amount > 0 ? `+${p.amount}` : String(p.amount),
                    p.reason,
                    new Date(p.createdAt).toLocaleDateString(),
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
